// skipcq: JS-W1028, JS-0128
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ref,
  listAll,
  getDownloadURL,
  uploadBytesResumable,
  deleteObject,
} from 'firebase/storage';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { storage, db } from '../utils/firebase';
import { EVENT_DEFINITIONS_COLLECTION } from '../utils/firestoreCollections';
import {
  EVENTS_STORAGE_FOLDER,
  sanitizeEventImageFilename,
  validateEventImageFile,
} from '../pages/events/eventImageUpload';

async function getDownloadUrlForListedItem(item) {
  const fullPath =
    typeof item?.fullPath === 'string' ? item.fullPath : null;

  const attempts = [
    { label: 'getDownloadURL(list item)', run: () => getDownloadURL(item) },
    ...(fullPath
      ? [
          {
            label: `getDownloadURL(ref(storage, "${fullPath}"))`,
            run: () => getDownloadURL(ref(storage, fullPath)),
          },
        ]
      : []),
  ];

  let lastErr = null;
  for (const { label, run } of attempts) {
    try {
      const url = await run();
      if (url) {
        return { url };
      }
    } catch (err) {
      lastErr = err;
      const code = err?.code ?? 'unknown';
      const message = err?.message ?? String(err);
      // eslint-disable-next-line no-console
      console.error(
        `[useEventStorageImages] getDownloadURL failed (${label})`,
        fullPath ?? item?.name,
        code,
        message,
        err,
      );
    }
  }

  const code = lastErr?.code ?? 'unknown';
  const message = lastErr?.message ?? 'Unknown error';
  return {
    url: null,
    resolveError: `${code}: ${message}`,
  };
}

/**
 * Lists event PNGs under Storage `events/`, resolves preview URLs, uploads with validation,
 * and deletes only when no event definition references the filename.
 */
function useEventStorageImages() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const filesRef = useRef(files);

  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const folderRef = ref(storage, EVENTS_STORAGE_FOLDER);
      const res = await listAll(folderRef);
      const items = await Promise.all(
        res.items.map(async (item) => {
          const resolved = await getDownloadUrlForListedItem(item);
          if (resolved.url) {
            return {
              name: item.name,
              url: resolved.url,
              urlError: false,
              resolveError: null,
            };
          }
          return {
            name: item.name,
            url: null,
            urlError: true,
            resolveError: resolved.resolveError ?? null,
          };
        }),
      );
      items.sort((a, b) => a.name.localeCompare(b.name));
      setFiles(items);
    } catch (err) {
      const code = err?.code ?? 'unknown';
      const message = err?.message ?? String(err);
      // eslint-disable-next-line no-console
      console.error(
        '[useEventStorageImages] Failed to list or load event images from storage.',
        code,
        message,
        err,
      );
      setError('Failed to load images from storage.');
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const fileNames = useMemo(() => files.map((f) => f.name), [files]);

  const imageUrlByName = useMemo(() => {
    const map = {};
    for (const f of files) {
      if (f.url) {
        map[f.name] = f.url;
      }
    }
    return map;
  }, [files]);

  const uploadFile = useCallback(
    (file, { onProgress, overwrite = false } = {}) => {
      const validation = validateEventImageFile(file);
      if (!validation.ok) {
        return { ok: false, message: validation.message };
      }

      const name = sanitizeEventImageFilename(file.name);
      if (!name) {
        return {
          ok: false,
          message:
            'Invalid file name. Use only letters, numbers, dots, dashes, underscores, end with .png.',
        };
      }

      const exists = filesRef.current.some((f) => f.name === name);
      if (exists && !overwrite) {
        return {
          ok: false,
          code: 'DUPLICATE',
          message: `An image named "${name}" already exists.`,
        };
      }

      const storageRef = ref(storage, `${EVENTS_STORAGE_FOLDER}/${name}`);

      return new Promise((resolve) => {
        const task = uploadBytesResumable(storageRef, file, {
          contentType: 'image/png',
          cacheControl: 'public, max-age=31536000',
        });
        task.on(
          'state_changed',
          (snap) => {
            const pct = Math.round(
              (snap.bytesTransferred / snap.totalBytes) * 100,
            );
            onProgress?.(pct);
          },
          (err) => {
            resolve({
              ok: false,
              message: err?.message || 'Upload failed. Please try again.',
            });
          },
          async () => {
            try {
              await refresh();
              onProgress?.(100);
              resolve({ ok: true, name });
            } catch (e) {
              resolve({
                ok: false,
                message: e?.message || 'Upload finished but refresh failed.',
              });
            }
          },
        );
      });
    },
    [refresh],
  );

  const deleteImageIfUnused = useCallback(
    async (objectName) => {
      const trimmed = (objectName ?? '').trim();
      if (!trimmed) {
        return { ok: false, message: 'Missing image name.' };
      }

      try {
        const byCard = query(
          collection(db, EVENT_DEFINITIONS_COLLECTION),
          where('cardImage', '==', trimmed),
          limit(25),
        );
        const byHeader = query(
          collection(db, EVENT_DEFINITIONS_COLLECTION),
          where('headerImage', '==', trimmed),
          limit(25),
        );
        const byLegacy = query(
          collection(db, EVENT_DEFINITIONS_COLLECTION),
          where('image', '==', trimmed),
          limit(25),
        );
        const [snapCard, snapHeader, snapLegacy] = await Promise.all([
          getDocs(byCard),
          getDocs(byHeader),
          getDocs(byLegacy),
        ]);
        const seenIds = new Set();
        const referencing = [];
        for (const snap of [snapCard, snapHeader, snapLegacy]) {
          for (const docSnap of snap.docs) {
            if (seenIds.has(docSnap.id)) continue;
            seenIds.add(docSnap.id);
            referencing.push({
              id: docSnap.id,
              name: docSnap.data().name || docSnap.id,
            });
          }
        }
        if (referencing.length > 0) {
          return { ok: false, referencing };
        }

        const storageRef = ref(storage, `${EVENTS_STORAGE_FOLDER}/${trimmed}`);
        await deleteObject(storageRef);
        await refresh();
        return { ok: true };
      } catch (e) {
        return {
          ok: false,
          message: e?.message || 'Could not delete image.',
        };
      }
    },
    [refresh],
  );

  return {
    files,
    fileNames,
    imageUrlByName,
    loading,
    error,
    refresh,
    uploadFile,
    deleteImageIfUnused,
  };
}

export default useEventStorageImages;
